import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../../../components/Utils/ModalShow";
import { FaCheck, FaDownload, FaUsers, FaCoins, FaCalculator, FaMagic } from "react-icons/fa";
import { apiService } from "../../../../components/Config/api.service";
import { signalerErreur } from "../../../../components/Utils/signalerErreur";
import { notify } from "../../../../components/Utils/Notifications";

// const iconMap = {
//   FaUsers: <FaUsers className="inline mr-1" />,
//   FaCoins: <FaCoins className="inline mr-1" />,
//   FaCalculator: <FaCalculator className="inline mr-1" />,
//   FaMagic: <FaMagic className="inline mr-1" />,
// };

const ExportAccountingModal = ({
  isOpen,
  onClose,
  categorizedRules,
  categoriesMeta = {},
  defaultCheckedColumns = ["name", "code", "account_debit", "account_credit", "category"],
}) => {
  const { t } = useTranslation();
  const ALL_COLUMNS = [
    { key: "name", label: t('exportAccountingModal.colName') },
    { key: "code", label: t('exportAccountingModal.colCode') },
    { key: "account_debit", label: t('exportAccountingModal.colDebitAccount') },
    { key: "account_credit", label: t('exportAccountingModal.colCreditAccount') },
    { key: "category", label: t('exportAccountingModal.colCategory') },
  ];
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState(defaultCheckedColumns);
  const [includeDefaults, setIncludeDefaults] = useState(true);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  // Récupère toutes les règles à plat
  const allRules = React.useMemo(() => {
    if (!categorizedRules) return [];
    return Object.values(categorizedRules)
      .flat()
      .filter(r => r.id && !String(r.id).startsWith("default_") && !r.is_handled_by_default);
  }, [categorizedRules]);

  // Filtre les règles par catégorie et par recherche
  const filteredRulesByCategory = React.useMemo(() => {
    if (!categorizedRules) return {};
    const result = {};
    Object.entries(categorizedRules).forEach(([cat, rules]) => {
      const filtered = rules.filter(
        r =>
          r.id &&
          !String(r.id).startsWith("default_") &&
          !r.is_handled_by_default &&
          (
            r.name.toLowerCase().includes(search.toLowerCase()) ||
            r.code.toLowerCase().includes(search.toLowerCase())
          )
      );
      if (filtered.length) result[cat] = filtered;
    });
    return result;
  }, [categorizedRules, search]);

  // Sélectionne tout par défaut à l'ouverture
  useEffect(() => {
    if (isOpen && allRules.length) {
      setSelectedIds(allRules.map(r => r.id));
    }
  }, [isOpen, allRules]);

  // Sélection globale
  const allColumnsSelected = selectedColumns.length === ALL_COLUMNS.length;
  const allRulesSelected = selectedIds.length === allRules.length && allRules.length > 0;

  const handleToggleRule = id => {
    setSelectedIds(ids =>
      ids.includes(id) ? ids.filter(i => i !== id) : [...ids, id]
    );
  };

  const handleToggleColumn = key => {
    setSelectedColumns(cols =>
      cols.includes(key) ? cols.filter(c => c !== key) : [...cols, key]
    );
  };

  const handleSelectAllColumns = () => {
    setSelectedColumns(allColumnsSelected ? [] : ALL_COLUMNS.map(col => col.key));
  };

  const handleSelectAllRules = () => {
    setSelectedIds(allRulesSelected ? [] : allRules.map(r => r.id));
  };

  const handleExport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedIds.length) params.append("ids", selectedIds.join(","));
      if (selectedColumns.length) params.append("columns", selectedColumns.join(","));
      params.append("include_defaults", includeDefaults ? "true" : "false");
      const res = await apiService.getElement(`/accounting/codes/export?${params.toString()}`);
      if (res.data && res.data.file) {
        const link = document.createElement("a");
        link.href = "data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64," + res.data.file;
        link.download = res.data.filename || "Codes_Comptables.xlsx";
        document.body.appendChild(link);
        link.click();
        link.remove();
        notify(t('exportAccountingModal.exportSuccess'), false, 2);
        onClose();
      } else {
        notify(t('exportAccountingModal.exportError'), false, 3);
      }
    } catch (e) {
      signalerErreur(e, 'ExportAccountingModal.handleExport');
      notify(t('exportAccountingModal.exportError'), false, 3);
    }
    setLoading(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('exportAccountingModal.title')}
      headerIcon={<FaDownload />}
      footer={
        <button
          className="px-6 py-2 rounded bg-[--color-primary] text-white flex items-center gap-2"
          onClick={handleExport}
          disabled={loading || selectedColumns.length === 0 || selectedIds.length === 0}
        >
          {loading ? (
            <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
          ) : (
            <FaCheck />
          )}
          {t('exportAccountingModal.export')}
        </button>
      }
    >
      <div className="mb-4">

        <label className="font-semibold mb-2 block">{t('exportAccountingModal.columnsToExport')}</label>
        {/* <label className="flex items-center gap-2 font-semibold cursor-pointer">
            <input
              type="checkbox"
              checked={allColumnsSelected}
              onChange={handleSelectAllColumns}
            />
            Tout sélectionner
          </label> */}
        <div className="flex flex-wrap gap-3 mb-2">
          {ALL_COLUMNS.map(col => (
            <label key={col.key} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedColumns.includes(col.key)}
                onChange={() => handleToggleColumn(col.key)}
              />
              {col.label}
            </label>
          ))}
        </div>
        
      </div>
      <div className="mb-4">
        <label className="font-semibold mb-2 block">
          {t('exportAccountingModal.rulesToExport')}
          <span className="ml-2 text-xs text-gray-500">
            {t('exportAccountingModal.selectedCount', { count: selectedIds.length })}
          </span>
        </label>
        <input
          className="mb-2 px-2 py-1 border rounded w-full text-sm"
          placeholder={t('exportAccountingModal.searchRulePlaceholder')}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div className="max-h-56 overflow-y-auto border rounded p-2 bg-gray-50">
          {Object.keys(filteredRulesByCategory).length === 0 && (
            <div className="text-gray-400 text-sm">{t('exportAccountingModal.noRuleFound')}</div>
          )}
          {Object.entries(filteredRulesByCategory).map(([cat, rules]) => {
            const meta = categoriesMeta[cat] || {};
            return (
              <div key={cat} className="mb-3">
                <div className="flex items-center justify-between mb-1">
                  <span className={`font-semibold flex items-center gap-2 ${meta.color ? `text-${meta.color}-700` : ""}`}>
                    {/* {iconMap[meta.icon] || null} */}
                    {meta.title || cat}
                  </span>
                  {/* ...bouton sélectionner/désélectionner... */}
                </div>
                <div className="grid grid-cols-2 gap-x-4">
                  {rules.map(rule => (
                    <label
                      key={rule.id}
                      className={`flex items-center gap-2 cursor-pointer mb-1 rounded px-1 py-0.5 hover:bg-blue-50 transition`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(rule.id)}
                        onChange={() => handleToggleRule(rule.id)}
                      />
                      <span className="text-sm">
                        {rule.name}
                        <span className="text-gray-400"> ({rule.code})</span>
                        {rule.is_default && (
                          <span className="ml-1 px-1 bg-purple-100 text-purple-700 rounded text-xs">{t('exportAccountingModal.default')}</span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="mb-2">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={includeDefaults}
            onChange={() => setIncludeDefaults(v => !v)}
          />
          {t('exportAccountingModal.includeDefaultCodes')}
        </label>
      </div>
    </Modal>
  );
};

export default ExportAccountingModal;